import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useInView, useReducedMotion, AnimatePresence } from 'framer-motion';
import FilmGrain from '../components/brand/FilmGrain';
import { trackEvent } from '../utils/analytics';

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */

const SERVICES = [
  {
    id:    'content',
    num:   '01',
    label: 'CONTENT CREATION',
    body:  'Short-form video, Reels, photo campaigns, and social content that actually stops the scroll. Built around your brand voice — not a template.',
  },
  {
    id:    'social',
    num:   '02',
    label: 'SOCIAL MEDIA MANAGEMENT',
    body:  'Strategy, scheduling, and ongoing content for brands that want a real presence. I\'ve grown accounts from zero into recognizable local and regional brands.',
  },
  {
    id:    'film',
    num:   '03',
    label: 'FILM & VIDEO PRODUCTION',
    body:  'Commercials, brand films, music videos, event coverage. Shot and edited cinematically — not like every other camera-for-hire job you\'ve seen.',
  },
  {
    id:    'brand',
    num:   '04',
    label: 'BRAND BUILDING',
    body:  'Before we shoot anything, we figure out who your customer is, what they respond to, and how to build content that moves the needle. Then we build toward that.',
  },
];

const WORK_CATEGORIES = [
  { id: 'film',    label: 'FILM',           desc: 'Commercials / music videos / brand films / events' },
  { id: 'content', label: 'CONTENT',        desc: 'Social campaigns / Reels / product content' },
  { id: 'brands',  label: 'BRANDS',         desc: 'Creative direction / visual identity / campaigns' },
  { id: 'collabs', label: 'COLLABORATIONS', desc: 'Artists / clothing / culture / interesting ideas' },
];

const BUDGET_RANGES = [
  'Under $500 / month',
  '$500 – $1,500 / month',
  '$1,500 – $5,000 / month',
  '$5,000+ / month',
  'One-time project — let\'s discuss',
  'Not sure yet',
];

const CLIENT_TYPES = [
  { label: 'BUSINESSES',          desc: 'Restaurants, dispensaries, retail, service businesses — any brand that needs to grow a real audience.' },
  { label: 'MUSICIANS & ARTISTS', desc: 'Music videos, EPKs, live content, and a social presence that actually represents your sound.' },
  { label: 'BRANDS',              desc: 'From identity and strategy to campaigns and content calendars. I work with brands at any stage.' },
  { label: 'ANYONE SERIOUS',      desc: 'If you have a product, a story, or a business worth building — and you\'re serious about it — we should talk.' },
];

/* ─────────────────────────────────────────────
   ANIMATION HELPER
───────────────────────────────────────────── */

function useReveal() {
  const ref      = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const reduced  = useReducedMotion();

  const slide = {
    initial:    { y: reduced ? 0 : '106%', opacity: reduced ? 0 : 1 },
    animate:    isInView ? { y: 0, opacity: 1 } : {},
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1] },
  };

  const fade = (delay = 0) => ({
    initial:    { opacity: 0, y: reduced ? 0 : 18 },
    animate:    isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.8, delay },
  });

  return { ref, isInView, slide, fade, reduced };
}

/* ─────────────────────────────────────────────
   NAV  — matches existing site logo style
───────────────────────────────────────────── */

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open,     setOpen]     = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 300);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-black/80 backdrop-blur-md border-b border-white/10' : ''}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">

          {/* Logo — matches existing Header.jsx style exactly */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="group flex items-center shrink-0 relative z-[60]"
            aria-label="Back to top"
          >
            <span className="text-base sm:text-xl md:text-2xl font-black tracking-tighter text-white uppercase italic leading-none">
              Blackmagic{' '}
              <span className="text-white/50 group-hover:text-white transition-colors duration-300">
                Films
              </span>
            </span>
          </button>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.2em]">
            {[['services','Services'],['proof','Our Work'],['contact','Work With Us']].map(([id, label]) => (
              <button key={id} onClick={() => go(id)}
                className="font-medium tracking-wide text-white/60 hover:text-white transition-colors whitespace-nowrap">
                {label}
              </button>
            ))}
            <button onClick={() => go('contact')}
              className="px-4 py-2 rounded-full border border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black transition-all font-bold tracking-widest text-xs uppercase whitespace-nowrap">
              Let's Talk
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex flex-col justify-center items-end gap-[5px] w-8 h-8 relative z-[60]"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
          >
            <motion.span animate={open ? { rotate: 45, y: 6, width: '28px' } : { rotate: 0, y: 0, width: '28px' }} className="block h-[1px] bg-white" />
            <motion.span animate={open ? { opacity: 0 } : { opacity: 1, width: '20px' }} className="block h-[1px] bg-white" />
            <motion.span animate={open ? { rotate: -45, y: -6, width: '28px' } : { rotate: 0, y: 0, width: '28px' }} className="block h-[1px] bg-white" />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black flex flex-col items-start justify-center pl-10">
            {[['services','SERVICES'],['proof','OUR WORK'],['contact','WORK WITH US']].map(([id, label], i) => (
              <motion.button key={id}
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                transition={{ delay: i * 0.07 }}
                onClick={() => go(id)}
                className="font-display text-[17vw] leading-[0.92] text-white hover:text-gold transition-colors text-left">
                {label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ─────────────────────────────────────────────
   HERO
───────────────────────────────────────────── */

function Hero() {
  const reduced = useReducedMotion();

  const slideUp = (delay = 0) => ({
    initial:    { y: reduced ? 0 : '105%' },
    animate:    { y: 0 },
    transition: { duration: 1, delay, ease: [0.16, 1, 0.3, 1] },
  });

  return (
    <section className="relative min-h-screen flex flex-col justify-end pb-16 md:pb-24 pt-20 sm:pt-24 px-6 md:px-14 overflow-hidden bg-black">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#080808] to-black" />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse 80% 50% at 20% 60%, rgba(200,169,110,0.05) 0%, transparent 65%)',
        }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />
      </div>

      <div className="relative z-10 max-w-6xl w-full">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="font-condensed text-white/30 tracking-[0.5em] text-[10px] mb-6"
        >
          PA / NJ — CONTENT · FILM · BRAND BUILDING
        </motion.p>

        <div className="overflow-hidden mb-3">
          <motion.h1 {...slideUp(0.1)}
            className="font-display text-[20vw] md:text-[14vw] lg:text-[12vw] leading-[0.82] text-white">
            I DON'T<br />JUST<br />SHOOT.
          </motion.h1>
        </div>

        <div className="overflow-hidden mb-8">
          <motion.p {...slideUp(0.3)}
            className="font-display text-[7vw] md:text-[4.5vw] text-gold leading-none">
            I BUILD YOUR BRAND.
          </motion.p>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
          className="font-sans text-white/70 text-base md:text-lg leading-relaxed max-w-xl mb-12"
        >
          Content creation, social media, film production, and brand strategy — for businesses,
          musicians, and anyone who needs more than just a camera person.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={() => {
              trackEvent('hero_cta_click', { page: 'start' });
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="font-condensed font-bold tracking-[0.25em] text-[11px] text-black bg-white px-10 py-4 hover:bg-gold transition-colors duration-300"
          >
            LET'S TALK →
          </button>
          <button
            onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
            className="font-condensed font-semibold tracking-[0.25em] text-[11px] text-white/40 hover:text-white px-10 py-4 transition-colors duration-300"
          >
            SEE WHAT I DO ↓
          </button>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="absolute bottom-10 right-8" aria-hidden="true">
        <motion.div
          animate={reduced ? {} : { scaleY: [0, 1, 0], originY: 0 }}
          transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
          className="w-px h-14 bg-gradient-to-b from-white/40 to-transparent"
        />
      </motion.div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   DIFFERENTIATOR
───────────────────────────────────────────── */

function Differentiator() {
  const { ref, slide, fade } = useReveal();

  return (
    <section ref={ref} className="py-28 md:py-44 px-6 md:px-14 overflow-hidden" style={{ backgroundColor: '#080808' }}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="overflow-hidden mb-8">
            <motion.h2 {...slide}
              className="font-display text-[11vw] md:text-[7vw] leading-[0.85] text-white">
              MOST PEOPLE<br />JUST SHOW UP<br />WITH A CAMERA.
            </motion.h2>
          </div>
        </div>

        <div>
          <motion.p {...fade(0.3)}
            className="font-display text-2xl md:text-3xl text-gold leading-tight mb-8">
            I show up with a plan.
          </motion.p>

          <div className="space-y-5 font-sans text-white/75 text-base md:text-[17px] leading-relaxed">
            {[
              'Before I touch a camera, I want to understand your business — who your customer is, what they respond to, and what kind of content actually moves the needle for you.',
              'I\'ve built social media presences for dispensaries, created campaigns for national brands, filmed for musicians, and helped businesses go from invisible to recognizable in their market.',
              'You\'re not just getting a shooter. You\'re getting someone who thinks about your brand the way you do — and who knows how to translate that into content people watch, share, and act on.',
            ].map((text, i) => (
              <motion.p key={i} {...fade(0.35 + i * 0.1)}>{text}</motion.p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SERVICES
───────────────────────────────────────────── */

function Services() {
  const { ref, isInView, slide, reduced } = useReveal();

  return (
    <section id="services" ref={ref} className="py-28 md:py-44 px-6 md:px-14 overflow-hidden bg-black">
      <div className="max-w-6xl mx-auto">
        <div className="overflow-hidden mb-16 md:mb-20">
          <motion.h2 {...slide}
            className="font-display text-[9vw] md:text-[5.5vw] leading-none text-white">
            WHAT I DO
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: reduced ? 0 : 24 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.12, duration: 0.8 }}
              className="border border-white/[0.14] bg-[#0d0d0d] p-8 md:p-10 group hover:border-white/30 hover:bg-[#111] transition-all duration-300"
            >
              <p className="font-condensed text-gold/70 tracking-[0.4em] text-xs mb-5">{s.num}</p>
              <h3 className="font-display text-3xl md:text-4xl text-white mb-5 leading-tight group-hover:text-gold transition-colors duration-300">
                {s.label}
              </h3>
              <p className="font-sans text-white/65 text-sm md:text-base leading-relaxed group-hover:text-white/85 transition-colors duration-300">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   CLIENT PROOF
───────────────────────────────────────────── */

const FEATURED_CLIENTS = [
  {
    id:     'jerseyjoint',
    name:   'JERSEY JOINT',
    type:   'Cannabis Dispensary — Glassboro, NJ',
    tags:   'Content Creation · Social Media · Brand Building · Event Coverage',
    accent: 'text-green-400/80',
    border: 'hover:border-green-900/60',
    body:   "Jersey Joint is a cannabis dispensary in Glassboro, NJ. I'm on their social media team and building out their content identity with educational series, event coverage, community focused storytelling and a consistent brand voice that shows on their Instagram.",
    logo:   '/images/clients/jersey-joint.png',
  },
  {
    id:     'frontsight',
    name:   'FRONT SIGHT COFFEE',
    type:   'Veteran-Owned Coffee Company',
    tags:   'Film Production · Brand Content · Commercial',
    accent: 'text-amber-400/80',
    border: 'hover:border-amber-900/60',
    body:   'Front Sight Coffee is a veteran-owned coffee brand with a strong identity and a loyal customer base. I came in to create cinematic brand content that matched the grit and purpose behind what they built. Shot and edited to feel premium and intentional — the kind of content that makes someone stop scrolling and actually feel something about a cup of coffee.',
    logo:     '/images/clients/front-sight-coffee.jpeg',
    circular: true,
  },
  {
    id:     'secretstash',
    name:   'THE SECRET STASH',
    type:   "Kevin Smith's Comic Book Store — Red Bank, NJ",
    tags:   'Brand Film · Event Coverage · Content',
    accent: 'text-purple-400/80',
    border: 'hover:border-purple-900/60',
    body:   "The Secret Stash is Kevin Smith's legendary comic book store in Red Bank, NJ — a pop culture institution with a massive following. I created brand film content and event coverage that captured the energy of the store and the community around it. The kind of place that has a real story, and needed visuals that actually did it justice.",
    logo:   '/images/clients/secret-stash-logo.png',
  },
];

const SMALL_CLIENTS = [
  { src: '/images/covers/poolscum.png',             label: 'POOL SCUM',          sub: 'Music Video' },
  { src: '/images/warped-tour/wtour7.JPG',           label: 'WARPED TOUR',        sub: 'Event Coverage' },
  { src: '/images/covers/kevinsmith-bestadvice.png', label: 'KEVIN SMITH',        sub: 'Documentary' },
  { src: '/images/covers/nichecommercial.png',       label: 'NICHE SOCCER',       sub: 'Commercial' },
  { src: '/images/covers/broncobrew.png',            label: 'BRONCO BREW',        sub: 'Brand Film' },
  { src: '/images/covers/kevinsmith-tusk.png',       label: 'TUSK',               sub: 'Film Content' },
];

function ClientCard({ client, index }) {
  const { ref, isInView, reduced } = useReveal();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: reduced ? 0 : 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.1, duration: 0.9 }}
      className={`border border-white/[0.18] p-8 md:p-12 transition-colors duration-500 ${client.border}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-center">
        <div className="md:col-span-2">
          <p className={`font-condensed tracking-[0.35em] text-xs mb-5 ${client.accent}`}>
            {client.tags}
          </p>
          <h3 className="font-display text-5xl md:text-6xl text-white mb-3 leading-none">
            {client.name}
          </h3>
          <p className="font-condensed text-white/55 tracking-wider text-sm mb-7">
            {client.type}
          </p>
          <p className="font-sans text-white/75 text-base md:text-[17px] leading-relaxed">
            {client.body}
          </p>
        </div>
        {/* Logo panel */}
        <div className="flex items-center justify-center p-6 md:p-8">
          {client.circular ? (
            <div className="w-44 h-44 md:w-52 md:h-52 rounded-full overflow-hidden flex-shrink-0">
              <img
                src={client.logo}
                alt={`${client.name} logo`}
                className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity duration-500"
                loading="lazy"
              />
            </div>
          ) : (
            <img
              src={client.logo}
              alt={`${client.name} logo`}
              className="w-full max-w-[220px] md:max-w-full h-auto max-h-48 object-contain opacity-90 hover:opacity-100 transition-opacity duration-500"
              loading="lazy"
            />
          )}
        </div>
      </div>
    </motion.div>
  );
}

function Proof() {
  const { ref, isInView, slide, reduced } = useReveal();

  return (
    <section id="proof" ref={ref} className="py-28 md:py-44 px-6 md:px-14 overflow-hidden" style={{ backgroundColor: '#090909' }}>
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end mb-16">
          <div className="overflow-hidden">
            <motion.h2 {...slide}
              className="font-display text-[9vw] md:text-[5.5vw] leading-none text-white">
              BRANDS I'VE<br />BUILT FOR
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-sans text-white/70 text-base md:text-lg leading-relaxed max-w-md"
          >
            From dispensaries to comic book stores to veteran-owned brands — here's what the work
            actually looks like and what it's built for each client.
          </motion.p>
        </div>

        {/* Featured clients — one per row */}
        <div className="space-y-4 mb-12">
          {FEATURED_CLIENTS.map((client, i) => (
            <ClientCard key={client.id} client={client} index={i} />
          ))}
        </div>

        {/* Secondary grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {SMALL_CLIENTS.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: reduced ? 0 : 16 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 + i * 0.07 }}
              className="relative overflow-hidden aspect-square bg-neutral-900 group"
            >
              <img src={item.src} alt={item.label}
                className="w-full h-full object-cover opacity-50 group-hover:opacity-85 img-cinematic"
                loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent flex flex-col justify-end p-3">
                <p className="font-condensed font-bold text-[10px] tracking-[0.2em] text-gold">{item.label}</p>
                <p className="font-sans text-white/60 text-[11px] mt-0.5">{item.sub}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   THE NEEDLE
───────────────────────────────────────────── */

function TheNeedle() {
  const { ref, slide, fade } = useReveal();

  return (
    <section ref={ref} className="py-28 md:py-44 px-6 md:px-14 overflow-hidden bg-black">
      <div className="max-w-6xl mx-auto">
        <div className="overflow-hidden mb-12">
          <motion.h2 {...slide}
            className="font-display text-[9vw] md:text-[6vw] leading-[0.85] text-white">
            I FIND WHAT<br />MOVES THE NEEDLE.
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-16">
          {[
            { step: 'UNDERSTAND', body: 'Every brand has a different customer. Before I create anything, I want to know who they are, where they spend time, and what makes them stop scrolling.' },
            { step: 'BUILD',      body: 'Content strategy, posting cadence, visual identity, video production — I put together a system that runs consistently, not just a one-time shoot.' },
            { step: 'MEASURE',    body: "We track what lands and cut what doesn't. Good content marketing isn't guesswork — it gets refined over time based on real data from your actual audience." },
          ].map((item, i) => (
            <motion.div key={item.step} {...fade(0.2 + i * 0.12)}
              className="border border-white/[0.14] bg-[#0d0d0d] p-8 md:p-10">
              <p className="font-display text-5xl text-gold/40 mb-4">0{i + 1}</p>
              <p className="font-condensed font-bold tracking-[0.3em] text-white text-sm mb-5">{item.step}</p>
              <p className="font-sans text-white/70 text-sm md:text-base leading-relaxed">{item.body}</p>
            </motion.div>
          ))}
        </div>

        <motion.blockquote {...fade(0.5)} className="border-l-2 border-gold pl-8 md:pl-12">
          <p className="font-display text-3xl md:text-4xl text-white leading-tight">
            "The goal isn't content for the sake of content.<br />
            It's content that actually builds your business."
          </p>
          <footer className="font-condensed text-white/30 tracking-[0.3em] text-[10px] mt-5">— MIKE KUPER</footer>
        </motion.blockquote>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   WHO I WORK WITH
───────────────────────────────────────────── */

function WhoIWorkWith() {
  const { ref, isInView, slide, reduced } = useReveal();

  return (
    <section ref={ref} className="py-28 md:py-44 px-6 md:px-14 overflow-hidden" style={{ backgroundColor: '#080808' }}>
      <div className="max-w-6xl mx-auto">
        <div className="overflow-hidden mb-16">
          <motion.h2 {...slide}
            className="font-display text-[9vw] md:text-[5.5vw] leading-none text-white">
            WHO I WORK WITH
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CLIENT_TYPES.map((type, i) => (
            <motion.div
              key={type.label}
              initial={{ opacity: 0, y: reduced ? 0 : 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.7 }}
              className="border border-white/[0.14] bg-[#0d0d0d] p-8 md:p-10 group hover:border-white/30 hover:bg-[#111] transition-all duration-300"
            >
              <h3 className="font-display text-3xl md:text-4xl text-white mb-4 leading-tight group-hover:text-gold transition-colors duration-300">
                {type.label}
              </h3>
              <p className="font-sans text-white/70 text-sm md:text-base leading-relaxed group-hover:text-white/90 transition-colors duration-300">
                {type.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   WORK WITH US  (merged contact + categories)
───────────────────────────────────────────── */

const INPUT_CLS = [
  'w-full bg-transparent border border-white/12 px-5 py-4',
  'font-sans text-white text-sm placeholder-white/60',
  'focus:outline-none focus:border-white/35 transition-colors duration-300',
].join(' ');

function WorkWithUs() {
  const { ref, isInView, slide, fade, reduced } = useReveal();
  const [selected, setSelected] = useState(null);
  const [form,     setForm]     = useState({ name: '', email: '', business: '', need: '', budget: '', message: '' });
  const [status,   setStatus]   = useState('idle');

  const setField = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const handleCategory = (id) => {
    const next = selected === id ? null : id;
    setSelected(next);
    if (next) trackEvent('category_select', { category: next, page: 'start' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    let utmParams = {};
    try {
      const raw = sessionStorage.getItem('utm_params');
      if (raw) utmParams = Object.fromEntries(new URLSearchParams(raw));
    } catch (_) {}
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          subject: `New Lead: ${form.name}${selected ? ` — ${selected}` : ''}`,
          from_name: 'Blackmagic Films Website',
          name: form.name,
          email: form.email,
          business: form.business || '—',
          category: selected || '—',
          need: form.need,
          budget: form.budget,
          message: form.message || '—',
          source: window.location.href,
          ...utmParams,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setStatus('success');
      trackEvent('lead_form_submit', { page: 'start', category: selected, budget: form.budget });
    } catch (err) {
      console.error('Form error:', err);
      setStatus('error');
    }
  };

  return (
    <section id="contact" ref={ref} className="py-28 md:py-44 px-6 md:px-14 bg-black overflow-hidden">
      <div className="max-w-5xl mx-auto">

        {/* Headline */}
        <div className="overflow-hidden text-center mb-6">
          <motion.h2 {...slide}
            className="font-display text-[10vw] md:text-[7vw] leading-[0.85] text-white">
            GOT SOMETHING<br />WORTH MAKING?
          </motion.h2>
        </div>

        <motion.p {...fade(0.35)}
          className="font-sans text-white/70 text-base md:text-lg leading-relaxed text-center max-w-xl mx-auto mb-16">
          I work with brands, musicians, businesses, and people who want to make something
          that doesn't look like everything else. Tell me what you're building.
        </motion.p>

        {/* Category selector */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
          {WORK_CATEGORIES.map((cat, i) => (
            <motion.button
              key={cat.id}
              initial={{ opacity: 0, y: reduced ? 0 : 18 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.45 + i * 0.07 }}
              type="button"
              onClick={() => handleCategory(cat.id)}
              aria-pressed={selected === cat.id}
              className={`p-5 border text-left transition-all duration-300 ${
                selected === cat.id
                  ? 'border-gold bg-gold/[0.1] text-white'
                  : 'border-white/[0.18] bg-[#0d0d0d] text-white/70 hover:border-white/40 hover:text-white'
              }`}
            >
              <p className="font-display text-xl leading-tight mb-2">{cat.label}</p>
              <p className="font-sans text-[12px] leading-snug mt-1 opacity-70">{cat.desc}</p>
            </motion.button>
          ))}
        </div>

        {/* Pull statement */}
        <motion.div {...fade(0.65)} className="text-center mb-14">
          <p className="font-display text-4xl md:text-5xl text-gold">LET'S MAKE SOMETHING.</p>
        </motion.div>

        {/* Form */}
        {status === 'success' ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-center py-20 border border-white/[0.08]"
          >
            <p className="font-display text-[72px] text-gold mb-4">SENT.</p>
            <p className="font-sans text-white/40">I'll be back in touch within 24 hours.</p>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: reduced ? 0 : 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.75, duration: 0.8 }}
            onSubmit={handleSubmit}
            noValidate
            className="space-y-3"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input type="text"  name="name"     placeholder="Your name"          autoComplete="name"  value={form.name}     onChange={setField('name')}     required className={INPUT_CLS} />
              <input type="email" name="email"    placeholder="Email"              autoComplete="email" value={form.email}    onChange={setField('email')}    required className={INPUT_CLS} />
            </div>
            <input  type="text"   name="business" placeholder="Business / brand name"                   value={form.business} onChange={setField('business')}          className={INPUT_CLS} />
            <input  type="text"   name="need"     placeholder="What do you need? (content, film, social, brand — all of the above?)"
              value={form.need} onChange={setField('need')} required className={INPUT_CLS} />

            <div className="relative">
              <select name="budget" value={form.budget} onChange={setField('budget')} required
                className={`${INPUT_CLS} appearance-none pr-10 cursor-pointer`} style={{ backgroundColor: 'transparent' }}>
                <option value="" disabled className="bg-black">Monthly budget or project size</option>
                {BUDGET_RANGES.map((r) => <option key={r} value={r} className="bg-black">{r}</option>)}
              </select>
              <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-white/30 text-xs">▾</span>
            </div>

            <textarea name="message" placeholder="Anything else I should know about your brand or goals"
              rows={4} value={form.message} onChange={setField('message')} className={`${INPUT_CLS} resize-none`} />

            {status === 'error' && (
              <p className="font-sans text-red-400/80 text-sm">
                Something went wrong — please try again or reach out on Instagram.
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              onClick={() => trackEvent('contact_cta_click', { page: 'start', category: selected })}
              className="w-full font-condensed font-bold tracking-[0.28em] text-[11px] text-black bg-white py-5 hover:bg-gold transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed mt-1"
            >
              {status === 'submitting' ? 'SENDING…' : 'START A CONVERSATION →'}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────── */

function Footer() {
  return (
    <footer className="bg-black border-t border-white/[0.05] py-12 px-6 md:px-14">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <span className="text-xl font-black tracking-tighter text-white uppercase italic leading-none">
            Blackmagic <span className="text-white/50">Films</span>
          </span>
          <p className="font-condensed text-white/25 tracking-[0.3em] text-[10px] mt-1">
            FILMMAKER / CREATOR / BUILDER — PA / NJ
          </p>
        </div>
        <div className="flex flex-col gap-2">
          {[
            ['@blackmagicfilms_',      'https://www.instagram.com/blackmagicfilms_/'],
            ['@theepitomeofevil',      'https://www.instagram.com/theepitomeofevil/'],
            ['@jerseyjointdispensary', 'https://www.instagram.com/jerseyjointdispensary/'],
          ].map(([handle, url]) => (
            <a key={handle} href={url} target="_blank" rel="noopener noreferrer"
              onClick={() => trackEvent('footer_instagram_click', { handle, page: 'start' })}
              className="font-condensed text-white/20 hover:text-white/60 transition-colors text-sm tracking-wider">
              {handle}
            </a>
          ))}
        </div>
        <p className="font-condensed text-white/12 text-[11px] tracking-wider">
          © {new Date().getFullYear()} MIKE KUPER
        </p>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────────
   PAGE
───────────────────────────────────────────── */

export default function Start() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('utm_source') || params.has('utm_medium')) {
        sessionStorage.setItem('utm_params', params.toString());
      }
    } catch (_) {}
  }, []);

  return (
    <>
      <Helmet>
        <title>Content Creation & Brand Building — Blackmagic Films</title>
        <meta name="description"
          content="Blackmagic Films offers content creation, social media marketing, film production, and brand building for businesses, musicians, and brands in NJ and PA. We don't just shoot — we build brands." />
        <link rel="canonical" href="https://www.blackmagicfilms.com/content-creation" />
        <meta property="og:title"       content="Content Creation & Brand Building — Blackmagic Films" />
        <meta property="og:description" content="We don't just shoot. Blackmagic Films builds your brand from the ground up — content, social media, film production, and strategy." />
        <meta property="og:type"        content="website" />
        <meta property="og:url"         content="https://www.blackmagicfilms.com/content-creation" />
        <meta property="og:image"       content="https://www.blackmagicfilms.com/images/og-image.jpg" />
        <meta name="theme-color"        content="#000000" />
      </Helmet>

      <div className="bg-black text-white overflow-x-hidden">
        <FilmGrain opacity={0.04} />
        <Nav />
        <main>
          <Hero />
          <Differentiator />
          <Services />
          <Proof />
          <TheNeedle />
          <WhoIWorkWith />
          <WorkWithUs />
        </main>
        <Footer />
      </div>
    </>
  );
}
